import React                                                from "react"
import { CMSPilotApis }                                        from "apis/cmsPilotAPI"
import { useGlobalConfig }                                  from "context/GlobalContext"
import { Box, 
         Typography,
         Card,
         CardContent,
         TextField,
         FormControl,
         InputLabel,
         Select,
         MenuItem,
         OutlinedInput,
         Chip,
         Alert,
         CircularProgress,
       }                                                    from '@mui/material'
import { APIData }                                          from 'types/cms/frontendTypes'
import { useDispatch,
         useSelector }                                      from 'react-redux'
import { ApiErrorInfo }                                     from "types/common/models"
import { setCMSPilotList, 
         selectCMSPilotList,
         selectCMSLocales,
         setLocales,
         upsertCMSPilot,
         setContentLimits
       }                                                    from "store/dataReducers/cmsPilotSlice"
import { useEffect, 
         useState 
       }                                                    from "react"
import { HomeProfileAPI }                                   from "apis/homeProfileAPI"
import { setHomeProfileMetadata }                           from "store/dataReducers/homeProfileSlice"
import { ApplianceProfileAPI }                              from "apis/applianceProfileAPI"
import { setApplianceProfileMetadata }                      from "store/dataReducers/applianceProfileSlice"
import WorkOutlineIcon                                      from '@mui/icons-material/WorkOutline';
import AddIcon                                              from '@mui/icons-material/Add';
import { BglPrimaryButton, BglPrimaryOutlinedButton }                                 from "components/button/BglButtons"
import { BglLoading }                                       from "components/loading/BglLoading"
import { ROUTE_TREE }                                       from "routes/AppRoutes"
import { CreateCMSPilot }                                      from "types/apis/cmsPilotInterfaces"
import { selectPilot } from "store/dataReducers/pilotsSlice"
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents'

export const CMSPilotHomePage = () => {

  const { rc } = useGlobalConfig();
  
  const dispatch = useDispatch();
  
  const utilityList = useSelector(selectCMSPilotList)
  const locales = useSelector(selectCMSLocales)
  
  const [isLoading, setLoading] = useState(true);
  const [isSwitchingUtility, setIsSwitchingUtility] = useState(false);
  const [error, setError] = useState<ApiErrorInfo | undefined>(undefined);
  
  // Create Pilot Form State
  const [isCreatingPilot, setIsCreatingPilot] = useState(false);
  const [createPilotForm, setCreatePilotForm] = useState<CreateCMSPilot.params>({
    sourcePilotId : '',
    supportedLocales: [],
    defaultLocale : '',
    targetPilotData: {
      pilotId : '',
      name : '',
    }
  });
  const [createPilotError, setCreatePilotError] = useState<string>();
  const [createPilotSuccess, setCreatePilotSuccess] = useState<string>();

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CMS.PROJECTS_LOADED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {

    const getUtilities = async () => {

      setLoading(true)

      const [ apMetadataResp, hpAttributesResp, pilotsResp, localesResp, contentLimitsResp ] = await Promise.all([
        ApplianceProfileAPI.getApplianceProfileMetadata(rc),
        HomeProfileAPI.getHomeProfileAttributes(rc),
        CMSPilotApis.getPilots(rc),
        CMSPilotApis.getSupportedLocales(rc),
        CMSPilotApis.getContentLimits(rc)
      ])

      if (pilotsResp.error || hpAttributesResp.error || apMetadataResp.error || localesResp.error || contentLimitsResp.error) {
        setError(pilotsResp.error || hpAttributesResp.error || apMetadataResp.error || localesResp.error || contentLimitsResp.error)
        return
      }

      dispatch(setApplianceProfileMetadata(apMetadataResp.data))
      dispatch(setHomeProfileMetadata(hpAttributesResp.data))
      dispatch(setCMSPilotList(pilotsResp.data!))
      dispatch(setContentLimits(contentLimitsResp.data!))
      dispatch(setLocales(localesResp as any))

      setError(undefined)
      setLoading(false)
    }

    if (!utilityList || utilityList.length === 0) {
      getUtilities()
    } else {
      setLoading(false)
    }

  }, [dispatch, rc, utilityList])

  const switchUtility = async (utility : APIData<"api::utility.utility">) => {

    setIsSwitchingUtility(true);
    
    try {
      await rc.session.selectCMSPilot(utility)
      dispatch(selectPilot({ projectId : utility.pilotId }))
      rc.uiRouter.navigate(ROUTE_TREE.CMS.CONTENT, {
        id : utility.documentId
      })
    } catch (err) {
      setError(err as ApiErrorInfo);
    } finally {
      setIsSwitchingUtility(false);
    }
  }

  // Create Pilot Form Handlers
  const handleFormFieldChange = (field: string, value: any) => {
    setCreatePilotForm(prev => ({
      ...prev,
      [field]: value
    }));
    setCreatePilotError(undefined);
    setCreatePilotSuccess(undefined);
  };

  const handleTargetPilotDataChange = (field: string, value: any) => {
    setCreatePilotForm(prev => ({
      ...prev,
      targetPilotData: {
        ...prev.targetPilotData,
        [field]: value
      }
    }));
    setCreatePilotError(undefined);
    setCreatePilotSuccess(undefined);
  };

  const handleCreatePilot = async () => {

    setIsCreatingPilot(true);
    setCreatePilotError(undefined);
    setCreatePilotSuccess(undefined);

    try {
      // Validate required fields
      if (!createPilotForm.sourcePilotId || 
          !createPilotForm.targetPilotData.pilotId || 
          !createPilotForm.defaultLocale || 
          !createPilotForm.supportedLocales ||
          createPilotForm.supportedLocales.length === 0) {
        setCreatePilotError('Please fill in all required fields');
        return;
      }

      const response = await CMSPilotApis.createPilot(rc, createPilotForm);

      if (response.error) {
        setCreatePilotError(response.error.message || 'Failed to create pilot');
        return;
      }

      rc.analytics.trackEvent(ANALYTICS_EVENTS.CMS.PROJECT_CREATED, {
        supportedLocaleCount: createPilotForm.supportedLocales.length,
        isAsync: !!response.data?.jobId,
      })

      // Handle response based on type
      if (response.data?.utility) {
        setCreatePilotSuccess('Pilot created successfully!');
        dispatch(upsertCMSPilot(response.data.utility));
        
        await rc.session.selectCMSPilot(response.data.utility);
        rc.uiRouter.navigate(ROUTE_TREE.CMS.CONTENT, {
          id: response.data.utility.documentId
        });
        
      } else if (response.data?.jobId) {
        rc.uiRouter.navigate(ROUTE_TREE.CMS.CREATION_STATUS, {
          id : JSON.stringify(response.data)
        });
      } else {
        setCreatePilotError('Unexpected response format');
        return;
      }
      
      // Reset form
      setCreatePilotForm({
        sourcePilotId: '',
        supportedLocales: [],
        defaultLocale: '',
        targetPilotData: {
          pilotId: '',
          name: '',
        }
      });

    } catch (err) {
      setCreatePilotError('An unexpected error occurred');
    } finally {
      setIsCreatingPilot(false);
    }
  };

  const UtilityCard = ({ utility }: { utility: APIData<"api::utility.utility"> }) => {
    const defaultImageUrl = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
    
    return (
      <Card 
        sx={{ 
          width: 330,
          height: 320,
          minHeight: 320,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
          }
        }}
        onClick={() => switchUtility(utility)}
      >
        {/* Background Image */}
        <Box
          sx={{
            height: 160,
            backgroundImage: `url(${utility.logoWeb?.url || defaultImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative'
          }}
        >
          {/* Overlay with Utility Name */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              padding: 2,
              color: 'white'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}> 
              <WorkOutlineIcon sx={{ fontSize: 40, marginRight: 1 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  textAlign: 'left',
                  fontWeight: 'bold',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                }}
              >
                {utility.name}
              </Typography>
            </div>
            
          </Box>
        </Box>
        
        {/* Card Content */}
        <CardContent sx={{ p: 2, height: 160, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ mb: 2, textAlign: 'left' }}
          >
            CMS for {utility.name}
          </Typography>
          
          <BglPrimaryButton>
            Open Project
          </BglPrimaryButton>
        </CardContent>
      </Card>
    )
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', overflow: 'auto' }}>
      {/* Loading Overlay for Utility Switching */}
      {isSwitchingUtility && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              padding: 3,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <BglLoading className="" />
          </Box>
        </Box>
      )}
      
      {
        isLoading ? 
        <div>Loading...</div> 
        
        : error ? 
        <div>{error.message}</div>

        :

        <React.Fragment>

        <Typography variant="h6" sx={{ fontWeight: 'bold', margin: 2, textAlign: 'left' }}>Projects Dashboard</Typography>
      
        {/* Utility Cards Grid */}
        <Box sx={{ margin: 2 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', 
            gap: 3 
          }}>
            {utilityList.map((utility) => (
              <UtilityCard key={utility.documentId} utility={utility} />
            ))}
          </Box>
        </Box>

        {rc.permissionsManager.canUtilityCreate() && (
        <React.Fragment>

        <Typography variant="h6" sx={{ fontWeight: 'bold', margin: 2, marginTop : 4, textAlign: 'left' }}>Create New Project</Typography>

        <Card sx={{ margin: 2}}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Project Details
              </Typography>
            </Box>

            {/* Error/Success Messages */}
            {createPilotError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createPilotError}
              </Alert>
            )}
            {createPilotSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {createPilotSuccess}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* First Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <FormControl fullWidth>
                    <InputLabel>Source Pilot</InputLabel>
                    <Select
                      value={createPilotForm.sourcePilotId}
                      onChange={(e) => handleFormFieldChange('sourcePilotId', e.target.value)}
                      label="Source Pilot"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {utilityList.map((utility) => (
                        <MenuItem key={utility.pilotId} value={utility.pilotId}>
                          {utility.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    fullWidth
                    label="Pilot ID *"
                    value={createPilotForm.targetPilotData.pilotId}
                    onChange={(e) => handleTargetPilotDataChange('pilotId', e.target.value)}
                    required
                  />
                </Box>
              </Box>

              {/* Second Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={createPilotForm.targetPilotData.name}
                    onChange={(e) => handleTargetPilotDataChange('name', e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                  <FormControl fullWidth required>
                    <InputLabel>Default Locale *</InputLabel>
                    <Select
                      value={createPilotForm.defaultLocale}
                      onChange={(e) => handleFormFieldChange('defaultLocale', e.target.value)}
                      label="Default Locale *"
                    >
                      {locales.map((locale) => (
                        <MenuItem key={locale.code} value={locale.code}>
                          {locale.name} ({locale.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Third Row */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                
                <Box sx={{ flex: '1 1 300px', width: '300px' }}>
                  <FormControl fullWidth required>
                    <InputLabel>Supported Locales *</InputLabel>
                    <Select
                      multiple
                      value={createPilotForm.supportedLocales}
                      onChange={(e) => handleFormFieldChange('supportedLocales', e.target.value)}
                      input={<OutlinedInput label="Supported Locales *" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const locale = locales.find(l => l.code === value);
                            return (
                              <Chip 
                                key={value} 
                                label={locale ? `${locale.name} (${locale.code})` : value} 
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {locales.map((locale) => (
                        <MenuItem key={locale.code} value={locale.code}>
                          {locale.name} ({locale.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <BglPrimaryOutlinedButton
                  onClick={() => {
                    setCreatePilotForm({
                      sourcePilotId: '',
                      supportedLocales: [],
                      defaultLocale: '',
                      targetPilotData: {
                        pilotId: '',
                        name: ''
                      }
                    });
                    setCreatePilotError(undefined);
                    setCreatePilotSuccess(undefined);
                  }}
                  disabled={isCreatingPilot}
                >
                  Reset
                </BglPrimaryOutlinedButton>
                <BglPrimaryButton
                  onClick={handleCreatePilot}
                  disabled={isCreatingPilot}
                  startIcon={isCreatingPilot ? <CircularProgress size={20} /> : <AddIcon />}
                >
                  {isCreatingPilot ? 'Creating...' : 'Create Project'}
                </BglPrimaryButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
        </React.Fragment>
        )}

      </React.Fragment>
  
    }
    </Box>
  )
}